import React, { Fragment } from 'react';
import i18n from '@i18next/i18n';
import Button from '@tencent/dui/esm/components/Button';
import ADD_ICON from '@tencent/docs-design-resources/pc/svg/bottombar_new_sheet.svg';
import {
    ThemeItem,
} from './theme-item';
import {
    CUSTOM_KEY,
    DEFAULT_CONFIG,
    THEME_MAP,
} from '@src/slide/application/service/theme-color-manager/theme-config';
import { IThemeSelectItem } from '@src/slide/domain/ui-model/element-modeles/Interface/theme-model.interface';
import {
    ThemeEditCustomDialog,
} from '../theme-edit-custom/theme-edit-custom-dialog';
import { reaction } from 'mobx';
import { IReactionDisposer } from 'mobx/lib/internal';
import { STSchemeColorValList } from '@src/slide/domain/ui-model/element-modeles/theme-model';
import { IThemeColorManager } from '@src/slide/application/service/theme-color-manager/index.interface';
import { IRightMenuManager } from '@src/slide/application/service/right-menu-manager/core/right-menu-manager.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';

interface IThemeListProps {
    themeType: string;
    selectedKey: string;
    isCustom: boolean;
    themeColorManager?: IThemeColorManager;
    rightMenuManager?: IDynamicService<IRightMenuManager>;
    menuManager?: IMenuManager;
    handleThemeSelect(theme?: IThemeSelectItem, selectedKey?: string): void;
}

interface IThemeListStates {
    editThemeKey?: string;
    isCreateTheme: boolean;
    editThemeItem: IThemeSelectItem;
}

const CUSTOM_THEME_LIMIT_NUMBER = 100;

// todo: 色值顺序映射，修改为下划线大写
const ColorOrderMap = {
    14: 1,
    15: 2,
    16: 3,
    17: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 10,
    11: 11,
    12: 12,
};

export class ThemeList extends React.Component<IThemeListProps, IThemeListStates> {
    private reactionDispose: IReactionDisposer;
    public constructor(props) {
        super(props);
        this.state = {
            editThemeItem: DEFAULT_CONFIG,
            isCreateTheme: false,
        };
        this.initEvent();
    }

    public componentWillUnmount() {
        this.reactionDispose?.();
    }

    public render() {
        const { selectedKey, isCustom } = this.props;
        const themeColorManager = this.getThemeColorManager();
        let themeList: Array<IThemeSelectItem>;
        if (isCustom) {
            themeList = themeColorManager?.customColorList || [];
        } else {
            themeList = (THEME_MAP[this.props.themeType] || []) as Array<IThemeSelectItem>;
        }

        return <Fragment>
            {
                isCustom && <div style={{
                    height: 30,
                    marginBottom: 12,
                }}>
                    <Button
                        disabled={themeList.length >= CUSTOM_THEME_LIMIT_NUMBER}
                        onClick={() => {
                            this.showCustomThemeDialog(true);
                        }}
                        type={'plain'}
                        icon={ADD_ICON}
                        style={{
                            height: 30,
                            width: '100%',
                            borderRadius: 4,
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            fontSize: 12,
                            fontWeight: 400,
                            color: 'rgba(0, 0, 0, 0.88)',
                        }}>
                        {
                            i18n.t('创建自定义颜色')
                        }
                    </Button>
                </div>
            }
            {
                themeList.map((themeItem) => {
                    const itemKey = `${themeItem.id}`;
                    return <ThemeItem
                        rightMenuManager={this.props.rightMenuManager}
                        themeColorManager={this.props.themeColorManager}
                        menuManager={this.props.menuManager}
                        canEdit={this.props.themeType === CUSTOM_KEY}
                        selected={itemKey === selectedKey}
                        key={itemKey}
                        themeItem={themeItem}
                        onClick={() => {
                            this.handleThemeClick(itemKey, themeItem);
                        }}
                    />;
                })
            }
            {
                themeColorManager?.themeColorCustomDialogVisible && <ThemeEditCustomDialog
                    isCreateTheme={this.state.isCreateTheme}
                    themeItem={this.state.editThemeItem}
                    onOk={this.handleOk}
                    onCancel={this.handleCancel}
                    updateThemeItem={(themeItem) => {
                        this.setState({
                            editThemeItem: themeItem,
                        });
                    }}
                    visible={!!themeColorManager?.themeColorCustomDialogVisible} />
            }
        </Fragment>;
    }

    private showCustomThemeDialog = (isCreateTheme: boolean) => {
        const themeColorManager = this.getThemeColorManager();
        requestAnimationFrame(() => {
            this.setState({
                isCreateTheme,
            });
            themeColorManager?.updateThemeColorCustomDialogVisible(true);
        });
    };

    private handleOk = (props: IThemeSelectItem) => {
        const themeColorManager = this.getThemeColorManager();
        if (this.state.isCreateTheme) {
            themeColorManager?.addCustomTheme(props);
        } else {
            themeColorManager?.updateCustomTheme(props);
            if (themeColorManager?.currentEditThemeKey === props.id) {
                this.props.handleThemeSelect(props);
            }
        }

        this.setState({
            isCreateTheme: false,
        });
        themeColorManager?.updateThemeColorCustomDialogVisible(false);
    };

    private handleCancel = () => {
        const themeColorManager = this.getThemeColorManager();
        themeColorManager?.updateThemeColorCustomDialogVisible(false);
        this.setState({
            isCreateTheme: false,
        });
    };

    private handleThemeClick = (itemKey: string, theme: IThemeSelectItem) => {
        this.props.handleThemeSelect(theme, itemKey);
    };

    private getThemeColorManager = () => this.props.themeColorManager;

    private initEvent = () => {
        const themeColorManager = this.getThemeColorManager();
        this.reactionDispose = reaction(() => [
            themeColorManager?.themeColorCustomDialogVisible,
            themeColorManager?.customColorList,
            themeColorManager?.customColorList.length,
        ], () => {
            const { isCreateTheme } = this.state;
            const themeColorManager = this.getThemeColorManager();
            const { currentEditThemeKey } = themeColorManager || {};

            let themeItem: IThemeSelectItem;
            if (isCreateTheme && this.props.selectedKey) {
                // @ts-ignore
                themeItem = this.getCreateThemeConfig();
            } else if (currentEditThemeKey) {
                themeItem = themeColorManager?.getCustomThemeById(currentEditThemeKey) || DEFAULT_CONFIG;
            } else {
                themeItem = DEFAULT_CONFIG;
            }
            this.setState({
                editThemeItem: themeItem,
            });

            if (themeColorManager?.customColorList.findIndex(item => item.id === this.props.selectedKey) === -1
                && this.props.themeType === CUSTOM_KEY) {
                this.props.handleThemeSelect(undefined, '');
            }

            this.updateComponent();
        });
    };

    private getCreateThemeConfig = () => {
        const currentThemeModel = this.getThemeColorManager()?.cloneUiDataService?.presentation.themeList[0];
        const themeList = currentThemeModel?.getAllThemeColorList() || [];
        const colorSchema: any = {};
        themeList?.sort((a, b) => ColorOrderMap[a.themeKey] - ColorOrderMap[b.themeKey]).forEach((theme) => {
            const key = STSchemeColorValList[theme.val];
            colorSchema[key] = {
                val: theme.val,
                color: theme.color,
            };
        });
        const themeItem: Omit<IThemeSelectItem, 'id' | 'title'> = {
            isVip: false,
            colorSchema,
        };
        return themeItem;
    };

    private updateComponent = () => {
        this.forceUpdate();
    };
}
